/* eslint-disable new-cap */
import { Box } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord, normalizeKey } from '@deity-io/di-data-basics';
import { isIDefinitionContainer } from '@deity-io/di-data-definitions';
import { PathItem } from '@deity-io/di-data-structure-editor';
import type { mxCell, mxGraph, mxGraphModel, mxPoint } from '@deity-io/mxgraph'; // <- import types only
import { Icon, Modal } from '@deity-io/portal-ui';
import type { IConnectPayload } from 'contexts/MappingContext';
import { MappingActionTypes, MappingContext } from 'contexts/MappingContext';
import { CACHE_FIRST, EDITOR_PATH_ID, ENTITY_PATH_ID } from 'contracts/constants';
import type { ICollectionType, ISchema } from 'contracts/interfaces';
import _ from 'lodash';
import type { FC } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Prompt, useLocation } from 'react-router-dom';
import { useGetMappingQuery } from 'services/graphql/generated/hooks';
import { addPorts, getMappingFieldPortProps, initLazyZoom, throttle } from 'services/mxGraph';
import mx from 'services/mxGraph/mx'; // <- import values from factory()
import { labelBuilder } from 'services/mxGraph/mxHelpers/labelBuilder';
import { getDefaultEdgeStyle, getDefaultVertexStyle } from 'services/mxGraph/mxHelpers/mx.defaults';
import { MappingSide } from 'services/mxGraph/mxHelpers/nodes.types';
import { getParams, typeGuard } from 'utils';
import { v4 } from 'uuid';
import EditProcessor from './EditMappingRelation/EditProcessors/EditProcessor';
import MappingEditorPanel from './MappingEditorPanel';
import { buildPath, flattenModelTree, getConnectionOptions } from './helpers/mappingHelpers';

interface IMappingEditorProps {
  locked?: boolean;
}
const MappingEditor: FC<IMappingEditorProps> = ({ locked }) => {
  const defaultRowHeight = 32;
  const { pathname } = useLocation();
  const ctx = useContext(MappingContext);
  const editorContainer = useRef<any>(null);
  const graph = useRef<mxGraph & { lazyZoom?: any }>();
  const model = useRef<mxGraphModel>();
  const parent: any = useRef();
  const vertices: any = useRef<{ source: {}; target: {} }>({ source: {}, target: {} });
  const edges: any = useRef({}); // an index over edges, indexed by dot separated path string
  const initRelationRender = useRef<boolean>(false);
  const params: any = getParams(pathname, [EDITOR_PATH_ID, ENTITY_PATH_ID]);
  const optimisticRender = false;
  const [activePanel, setActivePanel] = useState<string | undefined>(undefined);
  const initialPopupState = { open: false, checkedPaths: ['/'], key: v4(), processor: 'expression' };
  const [modelPopupState, setModelPopupSate] = useState<{
    key: string;
    open: boolean;
    path?: Array<string>;
    processor?: string;
    expression?: string;
    checkedPaths: Array<string>;
  }>({ ...initialPopupState });
  /**
   * Initial effect
   */
  useEffect(() => {
    if (!initRelationRender.current) {
      initGraph();
      graph.current?.model.beginUpdate();
      renderGraph();
      graph.current?.model.endUpdate();
      graph.current?.refresh();
      // executeLayout(
      //   () => renderGraph(),
      //   () => postRender(ctx?.state.mappingSchema as ISchema)
      // );

      /**
       * Hack to make the layZoom work. Force render.
       * Need a render to have graph.current set
       */
      setActivePanel(undefined);
    }
    initRelationRender.current = true;

    // Specify how to clean up after this effect:
    return function cleanup() {
      ctx?.dispatch({
        type: MappingActionTypes.CLEAR
      });
    };
  }, []);

  const validateEdge = (edge: mxCell, source: mxCell, target: mxCell): string | null => {
    if (!graph.current) {
      return null;
    }
    try {
      if (source.isSplitter === true) {
        // find new source
        const found = graph.current
          .getChildEdges(graph.current.getDefaultParent())
          .find(eg => eg.getId() === source.value.edgeId);
        if (found) {
          // eslint-disable-next-line prefer-destructuring
          source = found.source;
        }
      }
      const isValidSource = source.parent?.mappingSide === 'Source';
      const isValidTarget = target.parent?.mappingSide === 'Target';
      const sourcePath = buildPath(source.parent.value.path);
      const targetPath = buildPath(target.parent.value.path);
      if (!isValidSource) {
        return "Target structure can't be used as a source!";
      }
      if (!isValidTarget) {
        return 'Mapping to a source field is not allowed!';
      }

      if (ctx?.state.structureEditor) {
        const [canConnect, message] = ctx.state.structureEditor.validateConnect(sourcePath, targetPath);
        /**
         * This is where the "can't connect" popup comes from
         * Overwrite the mx.Error handler to provide better popup
         */
        if (!canConnect) {
          return ['Unable to connect', ...message].join(',');
        }

        return null;
      }

      return 'No editor loaded';
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Encountered an error while validating the edge', err);

      return 'We encountered a problem. Please reload';
    }
  };

  /**
   * Builds the popup menu
   */
  const menuFactory = (menu: any, cell: any, event: any) => {
    if (cell) {
      if (cell.edge === true) {
        if (!(locked || ctx?.state.locked)) {
          menu.addItem('Remove mapping', null, () => {
            ctx?.dispatch({
              type: MappingActionTypes.DISCONNECT,
              payload: {
                edge: {
                  sourcePath: cell.source.parent.value.path,
                  targetPath: cell.target.parent.value.path
                }
              }
            });
            if (optimisticRender) {
              const nestedMappingEdges = graph.current
                ?.getChildEdges(parent.current)
                .filter(c => c.source.parent.value.key?.startsWith(cell.source.parent.value.key));
              if (nestedMappingEdges) {
                removeEdgeReference(nestedMappingEdges);
              }
            }
            mx.mxEvent.consume(event);
          });
        }
        // menu.addItem(`${isEditable ? 'Edit' : 'View'} mapping`, null, () => {
        //   const selectedEdge: IMappingEdge = {
        //     sourcePath: cell.source.parent.value.path,
        //     targetPath: cell.target.parent.value.path
        //   };
        //   ctx?.dispatch({ type: MappingActionTypes.SELECT_EDGE, payload: selectedEdge });
        // });
        menu.addItem('Show mapping flow', null, () => {
          const state = graph.current?.view.getState(cell);
          graph.current?.orderCells(true, [cell]);
          if (state) {
            state.shape.node.getElementsByTagName('path')[0].removeAttribute('visibility');
            state.shape.node.getElementsByTagName('path')[0].setAttribute('stroke-width', '6');
            state.shape.node.getElementsByTagName('path')[0].setAttribute('stroke', 'lightGray');
            state.shape.node.getElementsByTagName('path')[1].setAttribute('class', 'flow');
          }
        });
      }
      if (cell?.mappingSide) {
        const { path } = cell.value;
        const isSource = cell.mappingSide === 'Source';
        if (ctx?.state.structureEditor && !(locked || ctx?.state.locked)) {
          // menu.addItem(`${isEditable ? 'Edit' : 'View'} mapping`, null, () => {
          //   const nestedMappingEdges = graph.current?.getChildEdges(graph.current.getDefaultParent()).filter(c => {
          //     let valid = false;
          //     if (isSource) {
          //       valid = c.source.parent.id === cell.id;
          //     } else {
          //       valid = c.target.parent.id === cell.id;
          //     }
          //     return valid && c.edge === true;
          //   }) as Array<mxCell>;
          //   if (nestedMappingEdges.length) {
          //     const selectedEdge: IMappingEdge = {
          //       sourcePath: nestedMappingEdges[0].source.parent.value.path,
          //       targetPath: nestedMappingEdges[0].target.parent.value.path
          //     };
          //     ctx?.dispatch({ type: MappingActionTypes.SELECT_EDGE, payload: selectedEdge });
          //   }
          // });

          const options = getConnectionOptions(ctx.state.structureEditor, path, isSource);
          const [canConnect, canConnectMessages] = options.canDanglingConnect;
          if (canConnect) {
            menu.addItem(`Create ${cell.mappingSide} Mapping`, null, () => {
              if (path) {
                ctx?.dispatch({
                  type:
                    cell.mappingSide === 'Source'
                      ? MappingActionTypes.CREATE_SOURCE_MAPPING
                      : MappingActionTypes.CREATE_TARGET_MAPPING,
                  payload: {
                    path
                  }
                });
              }
            });
            if (isSource) {
              menu.addItem(`Create other ${cell.mappingSide} Mapping`, null, () => {
                // should be if dict then list and vice versa
                if (path) {
                  ctx?.dispatch({
                    type: MappingActionTypes.CREATE_OTHER_SOURCE_MAPPING,
                    payload: {
                      path
                    }
                  });
                }
              });

              menu.addItem(`Create mapping processor`, null, () => {
                if (path) {
                  setModelPopupSate({ ...modelPopupState, open: true, path });
                  // ctx?.dispatch({
                  //   type: MappingActionTypes.CREATE_SOURCE_MAPPING,
                  //   payload: {
                  //     path
                  //   }
                  // });
                }
              });
            }
          }
          const [canDisconnect, canDisconnectMessages] = options.canDisconnect;
          if (canDisconnect) {
            menu.addItem(`Disconnect Mapping`, null, () => {
              if (path) {
                ctx?.dispatch({
                  type:
                    cell.mappingSide === 'Source'
                      ? MappingActionTypes.MAPPING_DISCONNECT_SOURCE
                      : MappingActionTypes.MAPPING_DISCONNECT_TARGET,
                  payload: {
                    path
                  }
                });
                if (optimisticRender) {
                  const nestedMappingEdges = graph.current
                    ?.getChildEdges(graph.current.getDefaultParent())
                    .filter(c => (cell.mappingSide === 'Source' ? c.source.parent : c.target.parent === cell));
                  if (nestedMappingEdges) {
                    removeEdgeReference(nestedMappingEdges);
                  }
                }
                mx.mxEvent.consume(event);
              }
            });
          }
          const [canRemove, canRemoveMessages] = options.canRemove;
          if (canRemove) {
            menu.addItem(`Remove mapping`, null, () => {
              if (path) {
                ctx?.dispatch({
                  type:
                    cell.mappingSide === 'Source'
                      ? MappingActionTypes.MAPPING_REMOVE_SOURCE
                      : MappingActionTypes.MAPPING_REMOVE_TARGET,
                  payload: {
                    path
                  }
                });
                if (optimisticRender) {
                  const nestedMappingEdges = graph.current
                    ?.getChildEdges(graph.current.getDefaultParent())
                    .filter(c => (cell.mappingSide === 'Source' ? c.source.parent : c.target.parent === cell));
                  if (nestedMappingEdges) {
                    removeEdgeReference(nestedMappingEdges);
                  }
                }
                mx.mxEvent.consume(event);
              }
            });
          }
        }
      }
      menu.addItem('Fit to View', null, () => {
        graph.current?.fit(1);
      });
    }
  };

  if (graph.current) {
    graph.current.popupMenuHandler.factoryMethod = menuFactory; // reset for the references in the functions
    graph.current.validateEdge = validateEdge; // reset for the references in the functions
    graph.current.convertValueToString = (cell: any) => labelBuilder(cell, ctx);
  }

  /**
   * On ctx?.state.structureHashId change
   */
  useEffect(() => {
    if (graph.current) {
      graph.current.model.beginUpdate();
      const nestedMappingEdges = graph.current.getChildEdges(graph.current.getDefaultParent());
      removeEdgeReference(nestedMappingEdges);

      renderInitialRelation(ctx?.state.mappingSchema as ISchema);

      validateFieldState();
      graph.current.model.endUpdate();
    }
  }, [ctx?.state.structureHashId]);

  // TODO: Will be implemented as a generic mxGraph initializer
  const initGraph = () => {
    model.current = new (mx as any).mxGraphModel();
    graph.current = new mx.mxGraph(editorContainer.current);
    graph.current.zoomFactor = 1.5;
    initLazyZoom(graph.current);

    parent.current = graph.current.getDefaultParent();
    mx.mxEdgeHandler.prototype.snapToTerminals = true;
    mx.mxGraphHandler.prototype.guidesEnabled = true;
    mx.mxConstants.GUIDE_COLOR = 'black';
    // mx.mxConstants.WORD_WRAP = 'break-word';
    mx.mxConstants.VERTEX_SELECTION_STROKEWIDTH = 0;
    mx.mxConstants.VERTEX_SELECTION_DASHED = false;
    mx.mxConstants.VERTEX_SELECTION_COLOR = '#f5f5f5';
    mx.mxClient.NO_FO = true;
    // (mx.mxClient as any).confirm = (message?: string): boolean => {
    //   console.log('OVERRIDDING', message);
    //   return true;
    // };
    mx.mxEvent.disableContextMenu(editorContainer.current);
    graph.current.setEnabled(true);
    graph.current.setCellsCloneable(false);
    graph.current.setCellsMovable(false);
    graph.current.setCellsDisconnectable(false);
    graph.current.setAllowDanglingEdges(false);
    graph.current.setCellsEditable(false);
    graph.current.setConnectable(!(locked || ctx?.state.locked));
    graph.current.setPanning(true);
    graph.current.setHtmlLabels(true);
    graph.current.setCellsDeletable(!(locked || ctx?.state.locked));
    graph.current.setCellsResizable(false);
    graph.current.setVertexLabelsMovable(false);

    // graph.current.cellsSelectable = false;
    graph.current.edgeLabelsMovable = false;
    graph.current.resetEdgesOnMove = true;
    graph.current.panningHandler.useLeftButtonForPanning = false;
    graph.current.keepSelectionVisibleOnZoom = false;
    // graph.current.centerZoom = true;
    graph.current.popupMenuHandler.autoExpand = true;
    graph.current.popupMenuHandler.factoryMethod = menuFactory;

    graph.current.foldingEnabled = false;
    graph.current.getStylesheet().putDefaultVertexStyle(getDefaultVertexStyle(graph.current));
    graph.current
      .getStylesheet()
      .putDefaultEdgeStyle(getDefaultEdgeStyle(graph.current, mx.mxConstants.EDGESTYLE_SIDETOSIDE));
    graph.current.getStylesheet().putCellStyle('vertex', getDefaultVertexStyle(graph.current));

    // return type set to any
    graph.current.convertValueToString = (cell: any) => labelBuilder(cell);
    graph.current
      .getSelectionModel()
      .addListener(mx.mxEvent.CHANGE, (sender: any, evt: any) => selectionModelCB(sender, evt));
    graph.current.getSelectionModel().singleSelection = true;
    graph.current.connectionHandler.addListener(mx.mxEvent.CONNECT, connectionCB);
    // graph.current.connectionHandler.isValidTarget = (cell): boolean => {
    //   console.log('isValidTarget');
    //   return true;
    // };
    graph.current.getModel().addListener(mx.mxEvent.CHANGE, modelChangeCB);
    graph.current.addListener(mx.mxEvent.CLICK, (sender, evt) => {
      let src = evt.properties?.event?.srcElement;
      if (src && graph.current) {
        if (src.tagName === 'path') {
          src = src.parentElement;
        }
        if (src.classList?.contains('chevronDown') || src.classList?.contains('chevronRight')) {
          toggleCollapse(evt.properties.cell);
        }
      }
    });

    // mx.mxEvent.addListener(window, 'ondrop', (event: any) => {
    //   console.log('dropped');
    // });
    graph.current.popupMenuHandler.factoryMethod = menuFactory; // reset for the references in the functions
    graph.current.validateEdge = validateEdge; // reset for the references in the functions
    const rubberBand = new mx.mxRubberband(graph.current);
  };

  const toggleCollapse = (cell: mxCell) => {
    model.current?.beginUpdate();
    const collapse = !(cell.value.collapsed === true);
    cell.value.collapsed = collapse;

    graph.current?.refresh(cell); // this refresh causes the chevron image to update
    const cellPath = cell.value.path?.join('.');
    let found = false;
    let foundLastChild = false;
    let dx = defaultRowHeight;
    const trailingCells: Array<mxCell> = [];
    const childCells: Array<mxCell> = [];

    const modifier = collapse ? -1 : 1;
    mapRecord<mxCell, any>(
      cell.mappingSide === 'Source' ? vertices.current?.source : vertices.current?.target,
      (item, key) => {
        if (!found) {
          // skip all incl found
          found = key === cellPath;
        } else if (!foundLastChild) {
          foundLastChild = !key.startsWith(cellPath);
          if (foundLastChild) {
            trailingCells.push(item);
            dx -= defaultRowHeight; // cell.geometry.height;
          } else {
            graph.current?.moveCells([item], 0, modifier * dx, false);
            dx += defaultRowHeight; // cell.geometry.height;
            childCells.push(item);
          }
        } else {
          trailingCells.push(item);
        }
      }
    );

    if (collapse) {
      graph.current?.orderCells(true, childCells);
    }
    graph.current?.moveCells(trailingCells, 0, modifier * dx, false);
    model.current?.endUpdate();
  };

  /**
   * Only used in validateFieldState()
   */
  const decorateSourceFieldState = (path: Array<string>, isMapped: boolean) => {
    const index = ctx?.state.structureEditor?.fromSourceIndex(path);

    let currentErrors: IPropertyBag;
    let currentErrorCount = 0;
    if (index && index.mapping && ctx?.state.validationMessages) {
      const mappingPath = ctx.state.structureEditor?.getMappingPath(index.mapping);
      if (mappingPath) {
        const mappingPathItem = PathItem.formArray(mappingPath);
        currentErrors = mapRecord(ctx.state.validationMessages, (message, messagePath) => {
          if (mappingPathItem.string === messagePath) {
            // eslint-disable-next-line no-plusplus
            currentErrorCount++;

            return message;
          }

          return undefined;
        });
      }
    }

    const pathStr = path.join('.');
    if (pathStr in vertices.current.source) {
      const { value } = vertices.current.source[pathStr];
      if (isMapped && index?.mapping) {
        value.mappingCoreType = index.mapping.coreType;
      } else {
        delete value.mappingCoreType;
      }
      value.isMapped = isMapped;
      value.hasError = currentErrorCount > 0;
      value.collapsible = index?.schema.coreType !== 'field';

      // fieldErrorCounter(path, ctx?.state.structureEditor as StructureEditor, isSource).processorError > 0;
      refreshCell(vertices.current.source[pathStr]);
    }
    if (edges.current[pathStr]) {
      edges.current[pathStr].hasError = currentErrorCount > 0;
      //  fieldErrorCounter(path, ctx?.state.structureEditor as StructureEditor, isSource).processorError > 0;
      refreshCell(edges.current[pathStr]);
    }
  };

  /**
   * Only used in validateFieldState()
   */
  const decorateTargetFieldState = (path: Array<string>, isMapped: boolean) => {
    const index = ctx?.state.structureEditor?.fromTargetIndex(path);

    let currentErrors: IPropertyBag;
    let currentErrorCount = 0;
    if (index && index.mappings.length && ctx?.state.validationMessages) {
      index.mappings.forEach(mapping => {
        const mappingPath = ctx.state.structureEditor?.getMappingPath(mapping);
        if (mappingPath) {
          const mappingPathItem = PathItem.formArray(mappingPath);
          currentErrors = mapRecord(ctx.state.validationMessages, (message, messagePath) => {
            if (mappingPathItem.string === messagePath) {
              // eslint-disable-next-line no-plusplus
              currentErrorCount++;

              return message;
            }

            return undefined;
          });
        }
      });
    }

    const pathStr = path.join('.');
    if (pathStr in vertices.current.target) {
      const { value } = vertices.current.target[pathStr];
      value.isMapped = isMapped;
      value.hasError = currentErrorCount > 0;
      value.collapsible = index?.schema.coreType !== 'field';

      //  fieldErrorCounter(path, ctx?.state.structureEditor as StructureEditor, isSource).processorError > 0;
      refreshCell(vertices.current.target[pathStr]);
    }
    if (edges.current[pathStr]) {
      edges.current[pathStr].hasError = currentErrorCount > 0;
      //  fieldErrorCounter(path, ctx?.state.structureEditor as StructureEditor, isSource).processorError > 0;
      refreshCell(edges.current[pathStr]);
    }
  };

  const validateFieldState = () => {
    if (ctx?.state.structureEditor) {
      const editor = ctx?.state.structureEditor;
      if (editor) {
        const source = editor.getSourceSchema();
        decorateSourceFieldState([], editor.fromSourceIndex([])?.mapping !== undefined);
        if (isIDefinitionContainer(source)) {
          source.items.filter((item, path, isOverload) => {
            const fieldInMapping = editor.fromSourceIndex([...path]);
            if (fieldInMapping?.mapping) {
              decorateSourceFieldState([...path], true);
            } else {
              decorateSourceFieldState([...path], false);
            }

            return undefined;
          });
        }
        const target = editor.getTargetSchema();
        decorateTargetFieldState([], editor.fromTargetIndex([])?.mappings.length !== 0); // this check and
        if (isIDefinitionContainer(target)) {
          target.items.filter((item, path, isOverload) => {
            const fieldInMapping = editor.fromTargetIndex([...path]);
            if (fieldInMapping?.mappings.length) {
              // this check and
              decorateTargetFieldState([...path], true);
            } else {
              decorateTargetFieldState([...path], false);
            }

            return undefined;
          });
        }
      }
    }
  };

  const createRow = (
    value: any,
    tag: string,
    // eslint-disable-next-line default-param-last
    connectable: boolean = true,
    point: { x: number; y: number },
    width: number,
    height: number,
    mappingSide?: MappingSide,
    alternateParent?: mxCell
  ) => {
    if (Array.isArray(value?.path)) {
      if (mappingSide === MappingSide.SOURCE) {
        const item = ctx?.state.structureEditor?.fromSourceIndex(value.path);
        if (item?.mapping) {
          value.mapping = {
            properties: item.mapping.config,
            coreType: item.mapping?.coreType
          };
        }
      }
    }

    const vertex = graph.current?.createVertex(
      alternateParent || parent.current,
      null,
      value,
      point.x,
      point.y,
      width,
      height,
      tag
    );
    if (vertex) {
      vertex.vertex = true;
      vertex.connectable = connectable;
      if (mappingSide) {
        vertex['mappingSide'] = mappingSide;
        vertex.value.isMapped = false;
        vertex.value.hasError = false;
      }
      vertex[tag] = true;

      return vertex;
    }
  };

  const resetSelection = () => {
    ctx?.dispatch({ type: MappingActionTypes.SELECT, payload: undefined });
  };

  /**
   * Do not use ctx in this callback, only cell values,
   * ctx gets bound to an old version
   * @param sender
   * @param evt
   */
  const selectionModelCB = (sender: any, evt: any) => {
    if (sender.cells.length) {
      const cell = (sender.cells as Array<mxCell>).shift();
      if (cell && cell.edge && ctx?.state.structureEditor) {
        ctx.dispatch({
          type: MappingActionTypes.SELECT,
          payload: {
            origin: 'edge',
            source: cell.source.parent.value.path,
            target: cell.target.parent.value.path
          }
        });
        if (activePanel !== 'SETTINGS') {
          setActivePanel('SETTINGS');
        }
      } else if (cell && cell.value.isMapped) {
        let foundEdge;
        // update the mxGraph selection with the edge
        /**
         * todo, n:n relations
         */
        cell.children
          .filter(child => child.isPort)
          .forEach(port => {
            if (port.edges) {
              port.edges.forEach(edge => {
                foundEdge = edge;
                graph.current?.getSelectionModel().addCell(edge);
              });
            }
          });
        if (foundEdge) {
          const isSource = cell.mappingSide === 'Source';
          ctx?.dispatch({
            type: MappingActionTypes.SELECT,
            payload: {
              origin: isSource ? 'source' : 'target',
              source: foundEdge.source.parent.value.path,
              target: foundEdge.target.parent.value.path
            }
          });
          if (activePanel !== 'SETTINGS') {
            setActivePanel('SETTINGS');
          }
        } else {
          const isSource = cell.mappingSide === 'Source';
          const payload = isSource
            ? { origin: 'source' as const, source: cell.value.path }
            : { origin: 'target' as const, target: cell.value.path };
          ctx?.dispatch({
            type: MappingActionTypes.SELECT,
            payload
          });
          if (activePanel !== 'SETTINGS') {
            setActivePanel('SETTINGS');
          }
        }
      } else {
        resetSelection();
      }
    } else {
      resetSelection();
    }
  };

  const connectionCB = (sender, evt) => {
    const edge = evt.getProperty('cell');
    const connectionPayload: IConnectPayload = {
      edge: { sourcePath: edge.source.parent.value.path, targetPath: edge.target.parent.value.path }
    };
    // a lookup dictionary to check dangling fields
    edges.current[edge.source.parent.value.key] = edge;

    ctx?.dispatch({
      type: MappingActionTypes.CONNECT,
      payload: connectionPayload
    });
  };

  const getRootName = (schema: ISchema, fallback: string = 'root'): string => {
    if (schema !== null && typeof schema._meta === 'object' && schema._meta !== null && 'name' in schema._meta) {
      return schema._meta.name;
    }

    return fallback;
  };

  const renderGraph = () => {
    parent.current = graph.current?.getDefaultParent();
    const flattenSourceSchema = {};
    const flattenTargetSchema = {};
    flattenModelTree(flattenSourceSchema, ctx?.state.sourceSchema as ISchema, []);
    flattenModelTree(flattenTargetSchema, ctx?.state.targetSchema as ISchema, []);
    const sourceHeader = addHeader(ctx?.state.sourceEntity?.name || '', { x: 80, y: 40 });
    const targetHeader = addHeader(ctx?.state.targetEntity?.name || '', { x: 800, y: 40 });

    const sourceRoot = addRootNode(
      getRootName(ctx?.state.sourceSchema!, normalizeKey(ctx?.state.sourceEntity?.name || '')),
      typeGuard(ctx?.state.sourceSchema!),
      { x: 80, y: 80 },
      MappingSide.SOURCE,
      vertices.current.source,
      sourceHeader
    );
    const targetRoot = addRootNode(
      getRootName(ctx?.state.targetSchema!, normalizeKey(ctx?.state.targetEntity?.name || '')),
      typeGuard(ctx?.state.targetSchema!),
      { x: 800, y: 80 },
      MappingSide.TARGET,
      vertices.current.target,
      targetHeader
    );

    renderModelTree(
      flattenSourceSchema,
      vertices.current.source,
      { x: 80, y: 110 },
      defaultRowHeight,
      MappingSide.SOURCE,
      sourceRoot
    );
    renderModelTree(
      flattenTargetSchema,
      vertices.current.target,
      { x: 800, y: 110 },
      defaultRowHeight,
      MappingSide.TARGET,
      targetRoot
    );
  };

  const renderModelTree = (
    schema: {},
    objectVertices: {},
    point: { x: number; y: number },
    rowHeight: number,
    mappingSide: MappingSide,
    alternateParent: mxCell | undefined
  ) => {
    let position = { ...point };
    const portObj = {
      port: {
        position: {
          x: getMappingFieldPortProps(mappingSide)[0],
          y: 0.5
        },
        color: getMappingFieldPortProps(mappingSide)[1],
        borderColor: getMappingFieldPortProps(mappingSide)[2],
        dimensions: {
          width: '16',
          height: '16'
        }
      }
    };
    Object.entries(schema).forEach(([k, v]: any) => {
      if (!graph.current) {
        return;
      }
      objectVertices[k] = createRow(
        {
          key: k,
          path: v.path,
          name: v.path[v.path.length - 1],
          properties: v.properties,
          orgPoint: {
            ...position,
            y: position.y + rowHeight
          }
        },
        'row',
        false,
        position,
        480,
        36,
        mappingSide,
        alternateParent
      );
      graph.current.addCell(objectVertices[k]);
      addPorts({ ...portObj }, objectVertices[k], graph.current);
      position = {
        ...position,
        y: position.y + rowHeight
      };
    });
  };

  const drawEdge = (fieldProps: any, sourcePath: string[], targetPath: string[]) => {
    const edge = new mx.mxCell(null, new mx.mxGeometry(0, 0, 0, 0));
    const sourceIndex = vertices.current.source[sourcePath.join('.')];
    const targetIndex = vertices.current.target[targetPath.join('.')];

    if (sourceIndex && targetIndex) {
      const sourcePort = vertices.current.source[sourcePath.join('.')].children[0];
      const targetPort = vertices.current.target[targetPath.join('.')].children[0];

      if (graph.current?.getEdgesBetween(sourcePort, targetPort).length) {
        return null;
      }
      edge.value = fieldProps;
      edge.edge = true;
      edge.hasError = false;
      edge.geometry.relative = true;
      graph.current?.addEdge(edge, parent.current, sourcePort, targetPort);
      if (fieldProps.edgeProperties?.points?.length) {
        const edgePoints: mxPoint[] = [];
        fieldProps.edgeProperties.points.forEach(p => {
          edgePoints.push(new mx.mxPoint(p.x, p.y));
        });
        edge.geometry.points = edgePoints;
      }

      // TODO decorate color only on differ
      graph.current?.setCellStyles(mx.mxConstants.STYLE_STROKECOLOR, fieldProps.edgeProperties?.color, [edge]);

      return edge;
    }
    if (sourceIndex) {
      // eslint-disable-next-line no-console
      console.warn(
        `Invalid target mapping field encountered on drawEdge "${sourcePath.join('.')}" to "${targetPath.join('.')}"`
      );
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        `Invalid source mapping field encountered on drawEdge "${sourcePath.join('.')}" to "${targetPath.join('.')}"`
      );
    }
  };
  const renderInitialRelation = (mappingSchema: ISchema | ICollectionType) => {
    const traverseAndRenderEdge = (schema: ISchema | ICollectionType) => {
      Object.entries(schema).forEach(([key, value], i) => {
        if (value && key !== '_meta') {
          const config = (value as any)?._meta ? (value as any)._meta : value;

          const source = _.get(config, ['edgeProperties', 'source']);
          const target = _.get(config, ['edgeProperties', 'target']);
          if (source && target) {
            const targets = Array.isArray(target) ? target : [target];
            targets.forEach(tgt => {
              const renderedEdge = drawEdge(config, source.slice(1).split('/'), tgt.slice(1).split('/'));
              if (renderedEdge) {
                edges.current[source.slice(1).split('/').join('.')] = renderedEdge;
                // a lookup dictionary to check dangling fields
                // targetEdges.current[target.slice(1).split('/').join('.')] = true;
              }
            });
          }
          if ((value as any)?._meta) {
            traverseAndRenderEdge(value as ICollectionType);
          }
        }
      });
    };
    traverseAndRenderEdge(mappingSchema);
  };

  const addHeader = (title: string, point: { x: number; y: number }) => {
    const row = createRow({ title }, 'header', false, point, 480, 40);
    if (row) {
      graph.current?.addCell(row);
      // const portObj = {
      //   port: {
      //     position: {
      //       x: getMappingFieldPortProps(mappingSide)[0],
      //       y: 0.5
      //     },
      //     color: getMappingFieldPortProps(mappingSide)[1],
      //     borderColor: getMappingFieldPortProps(mappingSide)[2],
      //     dimensions: {
      //       width: '16',
      //       height: '16'
      //     }
      //   }
      // };
      // addPorts(portObj, row, graph.current!);
      // objectVertices[''] = row;
    }

    return row;
  };

  const addRootNode = (
    title: string,
    type: string,
    point: { x: number; y: number },
    mappingSide: MappingSide,
    objectVertices: {},
    alternateParent: mxCell | undefined
  ) => {
    const row = createRow(
      { name: title, path: [], properties: { type, required: mappingSide === MappingSide.TARGET } },
      'row',
      false,
      point,
      480,
      40,
      mappingSide,
      alternateParent
    );

    if (row) {
      graph.current?.addCell(row);
      const portObj = {
        port: {
          position: {
            x: getMappingFieldPortProps(mappingSide)[0],
            y: 0.5
          },
          color: getMappingFieldPortProps(mappingSide)[1],
          borderColor: getMappingFieldPortProps(mappingSide)[2],
          dimensions: {
            width: '16',
            height: '16'
          }
        }
      };
      addPorts(portObj, row, graph.current!);
      objectVertices[''] = row;
    }

    return row;
  };

  const modelChangeCB = (sender, evt) => {
    const { changes } = evt.getProperty('edit');
    // handle any child change
    changes.forEach(c => {
      // edge point change
      if (c?.constructor.name !== 'mxGeometryChange') {
        return;
      }
      if (_.isEqualWith(c?.geometry?.points, c?.previous?.points)) {
        return;
      }
      if (!_.has(c, 'cell.source.parent.value.path')) {
        return;
      }
      const mapping = ctx?.state.structureEditor?.fromSourceIndex(c.cell.source.parent.value.path);
      if (!mapping?.mapping) {
        return;
      }

      const path = ctx?.state.structureEditor?.getMappingPath(mapping.mapping);
      if (path) {
        // Fix for collapse of item. removes the points.
        // We manually set them back again
        const points = (c?.geometry?.points as Array<mxPoint>).map(item => ({ x: item.x, y: item.y }));

        const oldPoints = (c?.previous?.points as Array<mxPoint>)?.map(item => ({ x: item.x, y: item.y }));

        if (c.cell?.edge === true && points && points.length === 0) {
          if (oldPoints && oldPoints.length > 0) {
            c.cell.geometry.points = oldPoints.map(point => new mx.mxPoint(point.x, point.y));
            graph.current?.refresh(c.cell);

            return;
          }
        }

        ctx?.dispatch({
          type: MappingActionTypes.UPDATE_DEFAULT,
          payload: {
            path,
            property: ['edgeProperties', 'points'],
            value: points
          }
        });
      }
    });
  };

  const refreshCell = (cell: mxCell) => {
    graph.current?.refresh(cell);
  };

  const removeEdgeReference = (nestedMappingEdges: mxCell[]) => {
    nestedMappingEdges.forEach(e => {
      if (e.source.parent.value.key) {
        _.unset(edges.current, e.source.parent.value.key);
      }
      // if (e.target.parent.value.key) {
      //   _.unset(targetEdges.current, e.target.parent.value.key);
      // }
    });
    graph.current?.removeCells(nestedMappingEdges);
  };

  const { data } = useGetMappingQuery({
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: params.entityId
    },
    ...CACHE_FIRST
  });

  const checkPrompt = (var1, var2): boolean =>
    JSON.stringify(var1) !== JSON.stringify(var2) && !(locked || ctx?.state.locked);

  const onToggleChecked = (path: Array<string>, checked: boolean) => {
    const pathStr = `/${path.join('/')}`;
    let newPaths: Array<string> | undefined;
    if (checked) {
      const found = modelPopupState.checkedPaths.find(item => item === pathStr);
      if (!found) {
        newPaths = [...modelPopupState.checkedPaths, pathStr];
      }
    } else if (pathStr !== '/') {
      newPaths = modelPopupState.checkedPaths.filter(item => item !== pathStr);
    }
    if (newPaths) {
      setModelPopupSate({ ...modelPopupState, checkedPaths: newPaths });
    }
  };

  return (
    <Box style={{ position: 'relative', overflow: 'hidden' }}>
      <Prompt
        when={checkPrompt(data?.getMapping.schema, ctx?.state.mappingSchema)}
        message={(location, action) => `Changes that you made to the mapping will not be saved.`}
      />
      <Box
        onWheel={throttle(graph.current?.lazyZoom, 50)}
        style={{
          height: 'calc(100vh - 167px)',
          width: '100vw',
          overflow: 'hidden',
          marginTop: '96px'
        }}
        ref={editorContainer}
      />
      {params.editor && (
        <MappingEditorPanel
          activePanel={activePanel}
          onOpenPanel={panel => {
            setActivePanel(panel);
          }}
        />
      )}
      {modelPopupState.open && modelPopupState.path && (
        <Modal
          isOpen={modelPopupState.open}
          onClose={() => setModelPopupSate({ ...modelPopupState, open: false })}
          // label="Create a Processor"
          title={`Create a Processor for field /${modelPopupState.path.join('/')}`}
          closeIcon={<Icon icon="close" />}
          confirmText="Create? "
          onConfirm={() => {
            ctx?.dispatch({
              type: MappingActionTypes.CREATE_SOURCE_PROCESSOR,
              payload: {
                type: modelPopupState.processor || 'expression',
                path: modelPopupState.path || [],
                expression: modelPopupState.expression || '',
                fields: [...modelPopupState.checkedPaths]
              }
            });
            setModelPopupSate({ ...initialPopupState });
          }}
        >
          <EditProcessor
            key={modelPopupState.key}
            availableProcessors={['expression', 'toString']}
            processorProperties={{}}
            schema={ctx?.state.structureEditor?.fromSourceIndex(modelPopupState.path)?.schema}
            onToggleChecked={onToggleChecked}
            checkedPaths={modelPopupState.checkedPaths}
            expression={modelPopupState.expression}
            processor={modelPopupState.processor}
            onChange={(path, value) => {
              if (path[0] === 'expression') {
                setModelPopupSate({ ...modelPopupState, expression: value });
              } else if (path[0] === 'type') {
                setModelPopupSate({ ...modelPopupState, processor: value });
              }
            }}
          />
        </Modal>
      )}
    </Box>
  );
};

export default MappingEditor;
